import { type MaybeRefOrGetter, ref } from "vue";

import api from "@/lib/api/base";
import { useDropzone, useFilePicker } from "@/lib/dropzone";
import { ok } from "@/lib/openapi";
import { showToast } from "@/lib/toasts";

interface UseImageDropOptions {
  target: MaybeRefOrGetter<HTMLElement | null>;
  onSuccess: (imageUrl: string) => void;
}

const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

export const useImageDrop = ({ target, onSuccess }: UseImageDropOptions) => {
  const uploading = ref(false);
  const handleFileDrop = async (files: null | File[]) => {
    if (!files || uploading.value) {
      return;
    }

    const file = files[0];
    if (file.type === "image/svg+xml" || file.name.endsWith(".svg")) {
      showToast({
        type: "error",
        title: "SVG files are not supported",
        message: "Please upload a PNG, JPG, or GIF instead.",
      });
      return;
    }

    try {
      uploading.value = true;

      const formData = new FormData();
      formData.set("image", file);

      const data = await ok(api.post("/images", { body: formData }));

      onSuccess(data.image);
    } catch (err) {
      console.error(err);

      showToast({
        type: "error",
        title: `Failed to upload image`,
        message: `Try again later.`,
      });
    } finally {
      uploading.value = false;
    }
  };

  const { isDropping } = useDropzone(target, {
    accept: ACCEPTED_IMAGE_TYPES,
    onDrop: handleFileDrop,
    multiple: false,
  });

  const { open: openFilePicker } = useFilePicker({
    accept: ACCEPTED_IMAGE_TYPES,
    onDrop: handleFileDrop,
    multiple: false,
  });

  return {
    uploading,
    isDropping,
    openFilePicker,
  };
};
